'use client'

import React, { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useFreezeBodyScroll } from 'hooks'

import { Icon, Logo } from 'components/ui'
import { Button, buttonMessages } from 'components/inputs'
import Navigation from 'compositions/Navigation/Navigation'
import LiveSwitcher from 'compositions/LiveSwitcher/LiveSwitcher'
import StaticNavigation from 'compositions/Navigation/StaticNavigation/StaticNavigation'

import Controls from '../Controls/Controls'
import { useWalletAuth } from 'src/hooks/useWalletAuth'
import { sendGTMEvent } from '@next/third-parties/google'
import { useUserContext } from 'src/contexts/user'

const Content: React.FC = () => {
  useFreezeBodyScroll()

  return (
    <div className="fixed top-[54px] bottom-0 left-0 nr:w-[22.5rem] mb:w-full bg-bg-l0">
      <LiveSwitcher />
      <Navigation className="my-2 h-[calc(100vh_-_19rem)] overflow-y-scroll no-scrollbar" />
      <StaticNavigation />
    </div>
  )
}

const Header: React.FC = () => {
  const { isLoginComplete, isUserConnecting } = useUserContext()
  const { isReconnecting, isConnecting, connectOrCreateWallet } =
    useWalletAuth()

  const pathname = usePathname()
  const [isVisible, setVisibility] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const handleClick = () => {
    setVisibility(v => !v)
  }

  useEffect(() => {
    if (isVisible) {
      const handleOutsideClick = (event: MouseEvent) => {
        const composedPath = event.composedPath()

        if (!composedPath.includes(containerRef.current!)) {
          setVisibility(false)
        }
      }

      document.addEventListener('click', handleOutsideClick, { capture: true })

      return () => {
        document.removeEventListener('click', handleOutsideClick, {
          capture: true,
        })
      }
    }
  }, [isVisible])

  useEffect(() => {
    setVisibility(false)
  }, [pathname])

  return (
    <div ref={containerRef} className="py-2 px-5 bg-bg-l0">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div onClick={handleClick}>
            <Icon
              className="text-grey-60 h-6 w-6 mr-3"
              name={isVisible ? 'interface/close' : 'interface/burger_menu'}
            />
          </div>
          <Logo className="h-4" />
        </div>
        {Boolean(isMounted.current && isLoginComplete && !isUserConnecting) ? (
          <Controls />
        ) : (
          <Button
            className="ml-auto"
            title={buttonMessages.connectWallet}
            size={32}
            loading={isConnecting || isReconnecting || isUserConnecting}
            onClick={() => {
              connectOrCreateWallet()
              sendGTMEvent({
                event: 'wallet_connect_initiated',
                is_betslip: false,
                section: 'header',
                login_toolkit: 'privy',
              })
            }}
          />
        )}
      </div>
      {isVisible && <Content />}
    </div>
  )
}

export default Header
